import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { FormEachNode } from '../../types/layout';
import type { Condition } from '../../types/conditions';
import { evaluateCondition, type ConditionContext } from '../../evaluators/conditionEvaluator';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { FormEachInlineRenderer } from './FormEachInlineRenderer';
import { FormEachTagRowRenderer } from './FormEachTagRowRenderer';
import { FormEachCardRenderer } from './FormEachCardRenderer';
import { FormEachTableRenderer } from './FormEachTableRenderer';
import { FormEachAccordionRenderer } from './FormEachAccordionRenderer';
import { FormEachDefaultRenderer } from './FormEachDefaultRenderer';

interface FormEachRendererProps {
  node: FormEachNode;
}

export const FormEachRenderer = React.memo(function FormEachRenderer({ node }: FormEachRendererProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();
  const formValues = useWatch({ control: form.control });

  const isHidden = useMemo(() => {
    if (!node.visibleWhen) return false;
    const values = (formValues ?? form.getValues()) as Record<string, unknown>;
    const ctx: ConditionContext = { values, meta, getOptionProp };
    return !evaluateCondition(node.visibleWhen as unknown as Condition, ctx);
  }, [node.visibleWhen, formValues, form, meta, getOptionProp]);

  if (isHidden) return null;

  switch (node.presentation) {
    case 'inline':
      return <FormEachInlineRenderer node={node} />;
    case 'tag-row':
      return <FormEachTagRowRenderer node={node} />;
    case 'card':
      return <FormEachCardRenderer node={node} />;
    case 'table':
      return <FormEachTableRenderer node={node} />;
    case 'accordion':
      return <FormEachAccordionRenderer node={node} />;
    case 'default':
    case undefined:
      return <FormEachDefaultRenderer node={node} />;
    default: {
      const _exhaustive: never = node.presentation;
      void _exhaustive;
      return <FormEachDefaultRenderer node={node} />;
    }
  }
});
